<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import PriceRange from '@/components/SearchFilters/ModalContent/PriceRange/index.vue'
import FormChipSelector from '@/components/BrandedV2/FormSearchFilters/ChipSelector/index.vue'
import FormAreaRange from '@/components/BrandedV2/FormSearchFilters/AreaRange/index.vue'
import ClustersSelector from '@/components/SearchFilters/ModalContent/ClustersSelector/index.vue'
import useFormSearch from '@/composables/BrandedV2/use-form-search'
import type { FormSearchFiltersComponentModule } from '@/components/BrandedV2/FormSearchFilters/types'
import { isUSCity } from '@/constants/us-cities'

const props = defineProps<FormSearchFiltersComponentModule.Props>()

const { currentFilters, touchedFields } = useFormSearch()
const { t, tm } = useI18n()
const route = useRoute()

// Check if this is a US city page to use USD labels
const isUSCityPage = computed(() => {
  const cityParam = route.query.city as string | undefined
  return isUSCity(cityParam)
})

// Get price labels based on city
const priceRangeTitle = computed(() => {
  if (isUSCityPage.value) {
    return t('priceRange.label', {}, { locale: 'us' })
  }
  return t('priceRange.label')
})

const minPriceLabel = computed(() => {
  if (isUSCityPage.value) {
    return t('priceRange.min', {}, { locale: 'us' })
  }
  return t('priceRange.min')
})

const maxPriceLabel = computed(() => {
  if (isUSCityPage.value) {
    return t('priceRange.max', {}, { locale: 'us' })
  }
  return t('priceRange.max')
})

const deliveryTermSelector = ref<InstanceType<typeof FormChipSelector> | null>(null)
const bedroomsSelector = ref<InstanceType<typeof FormChipSelector> | null>(null)
const bathroomsSelector = ref<InstanceType<typeof FormChipSelector> | null>(null)
const parkingLotsSelector = ref<InstanceType<typeof FormChipSelector> | null>(null)

const deliveryTermOptions: any[] = tm('deliveryTerm.options')
const bedroomsOptions: any[] = tm('bedrooms.options')
const bathroomsOptions: any[] = tm('bathrooms.options')
const parkingLotsOptions: any[] = tm('parkingLots.options')

const dataAttributes = computed(() => {
  return { dataLh: { P: props.screenName, Z: 'modal_filtros' } }
})
const onChangeClusterSelector = (params: { isSelected: boolean, key: 'businessHubs' | 'neighborhoods' }) => {
  touchedFields.value[params.key] = params.isSelected
}

watch(() => props.resetTrigger, (newValue) => {
  deliveryTermSelector.value?.resetSelectedItems()
  bedroomsSelector.value?.resetSelectedItems()
  bathroomsSelector.value?.resetSelectedItems()
  parkingLotsSelector.value?.resetSelectedItems()
})
</script>

<script lang="ts">
export default {
  name: 'FormSearchFilters'
}
</script>

<template>
  <div class="form-filters__body">
    <template v-if="!isUSCityPage">
      <h4 class="form-filters__title-section">
        {{ t('location') }}
      </h4>

      <div class="mb-40">
        <ClustersSelector
          :id="`${screenName}-cluster-selector`"
          v-model="currentFilters"
          enable-defaults
          :data-attributes="dataAttributes"
          class="w-full"
          @change="onChangeClusterSelector" />
      </div>

      <hr class="border-gray2-600">
    </template>

    <div class="my-40">
      <h4 class="form-filters__title-section">
        {{ priceRangeTitle }}
      </h4>

      <PriceRange
        :id="`${screenName}-modal-filters-price-range-chip`"
        v-model:min-price="currentFilters.price.min"
        v-model:max-price="currentFilters.price.max"
        :max-price-label="maxPriceLabel"
        :min-price-label="minPriceLabel"
        :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, Z: `${dataAttributes.dataLh.Z}_${t('priceRange.label')}` }}"
        @change="touchedFields.price = true" />
    </div>

    <hr class="border-gray2-600">

    <div class="my-40">
      <h4 class="form-filters__title-section">
        {{ t('deliveryTerm.label') }}
      </h4>

      <FormChipSelector
        :id="`${screenName}-modal-filters-delivery-term-chip`"
        ref="deliveryTermSelector"
        v-model="currentFilters.deliveryTerm"
        :options="deliveryTermOptions"
        class="form-filters__delivery-term"
        :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, Z: `${dataAttributes.dataLh.Z}_${t('deliveryTerm.label')}` }}"
        @change="touchedFields.deliveryTerm = true" />
    </div>

    <hr class="border-gray2-600">

    <div class="my-40">
      <h2 class="form-filters__title-section">
        {{ t('characteristics') }}
      </h2>

      <p class="form-filters__input-label">
        {{ t('bedrooms.label') }}
      </p>

      <FormChipSelector
        :id="`${screenName}-modal-filters-bedrooms-number-chip`"
        ref="bedroomsSelector"
        v-model="currentFilters.bedroomsNumber"
        :options="bedroomsOptions"
        class="form-filters__tags"
        :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, Z: `${dataAttributes.dataLh.Z}_${t('bedrooms.label')}` }}"
        @change="touchedFields.bedroomsNumber = true" />
    </div>

    <hr class="border-gray2-600">

    <div class="my-40">
      <p class="form-filters__input-label">
        {{ t('parkingLots.label') }}
      </p>

      <FormChipSelector
        :id="`${screenName}-modal-filters-parking-lot-number-chip`"
        ref="parkingLotsSelector"
        v-model="currentFilters.parkingLotsNumber"
        :options="parkingLotsOptions"
        class="form-filters__tags"
        :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, Z: `${dataAttributes.dataLh.Z}_${t('parkingLots.label')}` }}"
        @change="touchedFields.parkingLotsNumber = true" />
    </div>

    <hr class="border-gray2-600">

    <div class="my-40">
      <p class="form-filters__input-label">
        {{ t('bathrooms.label') }}
      </p>

      <FormChipSelector
        :id="`${screenName}-modal-filters-bathrooms-number-chip`"
        ref="bathroomsSelector"
        v-model="currentFilters.bathroomsNumber"
        :options="bathroomsOptions"
        class="form-filters__tags"
        :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, Z: `${dataAttributes.dataLh.Z}_${t('bathrooms.label')}` }}"
        @change="touchedFields.bathroomsNumber = true" />
    </div>

    <hr class="border-gray2-600">

    <div class="mt-40">
      <h2 class="form-filters__title-section">
        {{ t('areaRange.label') }}
      </h2>

      <FormAreaRange
        :id="`${screenName}-modal-filters-area-range-chip`"
        v-model:min-area="currentFilters.area.min"
        v-model:max-area="currentFilters.area.max"
        :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, Z: `${dataAttributes.dataLh.Z}_${t('areaRange.label')}` }}"
        @change="touchedFields.area = true" />
    </div>
  </div>
</template>

<style src="./_index.scss"></style>
<i18n src="./i18n.json"></i18n>
